/**
 * Sources de recherche d'adresse du CustomAddressField.
 * La source dépend du type de client : une seule est interrogée à la fois, jamais les deux en parallèle.
 */

export const ADDRESS_SOURCE = {
    FRENCH: 'FRENCH',               // Base Adresse Nationale (IGN, Géoplateforme)
    INTERNATIONAL: 'INTERNATIONAL'  // Photon (komoot)
};

const FRENCH_COMPANY_TYPE = 'FRENCH_COMPANY'; // = CLIENT_TYPE_ENUM.FRENCH_COMPANY.value (utils.js)
const SUGGESTIONS_LIMIT = 5;

/**
 * Entreprise française : source française. Tout le reste (entreprise internationale, particulier, inconnu,
 * type non renseigné, ou aucun type fourni) : source internationale.
 */
export function getAddressSourceForClientType(clientType) {
    return clientType === FRENCH_COMPANY_TYPE ? ADDRESS_SOURCE.FRENCH : ADDRESS_SOURCE.INTERNATIONAL;
}

export function buildAddressSearchUrl(source, query) {
    const q = encodeURIComponent(query);
    return source === ADDRESS_SOURCE.FRENCH
        ? `https://data.geopf.fr/geocodage/search/?q=${q}&limit=${SUGGESTIONS_LIMIT}&autocomplete=1`
        : `https://photon.komoot.io/api/?q=${q}&limit=${SUGGESTIONS_LIMIT}`;
}

function mapInternationalFeature(result) {
    const properties = result.properties || {};
    const coordinates = result.geometry?.coordinates || [];
    return {
        ...result,
        label: [
            properties.housenumber,
            properties.street || properties.name,
            properties.postcode,
            properties.city || properties.town || properties.village,
            properties.country
        ].filter(Boolean).join(' '),
        street: properties.street || properties.name || '',
        streetNumber: properties.housenumber || '',
        postalCode: properties.postcode || '',
        city: properties.city || properties.town || properties.village || '',
        country: properties.country || '',
        countryCode: properties.countrycode ? properties.countrycode.toUpperCase() : '',
        latitude: coordinates[1],
        longitude: coordinates[0]
    };
}

// La BAN ne renvoie ni pays ni code pays : l'adresse est forcément française
function mapFrenchFeature(result) {
    const properties = result.properties || {};
    const coordinates = result.geometry?.coordinates || [];
    const isStreetLevel = properties.type === 'housenumber' || properties.type === 'street' || properties.type === 'locality';
    return {
        ...result,
        label: properties.label || '',
        street: properties.street || (isStreetLevel ? properties.name : '') || '',
        streetNumber: properties.housenumber || '',
        postalCode: properties.postcode || '',
        city: properties.city || '',
        country: 'France',
        countryCode: 'FR',
        latitude: coordinates[1],
        longitude: coordinates[0]
    };
}

/**
 * Convertit la réponse GeoJSON d'une source au format de suggestion attendu par le composant
 * (label, street, streetNumber, postalCode, city, country, countryCode, latitude, longitude).
 */
export function mapAddressFeatures(source, data) {
    const mapFeature = source === ADDRESS_SOURCE.FRENCH ? mapFrenchFeature : mapInternationalFeature;
    return (data?.features || []).map(mapFeature).filter(suggestion => suggestion.label);
}

/**
 * Interroge UNE seule source. Retourne [] si la source répond en erreur ;
 * les erreurs réseau (dont l'annulation) sont levées à l'appelant.
 */
export async function fetchAddressSuggestions(source, query, signal, fetchFn = (...args) => fetch(...args)) {
    const response = await fetchFn(buildAddressSearchUrl(source, query), {signal});
    if (!response.ok) {
        return [];
    }
    return mapAddressFeatures(source, await response.json());
}

/**
 * Garde contre les réponses tardives : seule la dernière requête démarrée reste "courante".
 * Démarrer une requête (ou invalider) annule la précédente et rend sa réponse inexploitable.
 */
export class AddressSearchSession {
    constructor() {
        this.counter = 0;
        this.controller = null;
    }

    begin() {
        this.invalidate();
        const id = this.counter;
        this.controller = new AbortController();
        return {
            signal: this.controller.signal,
            isCurrent: () => id === this.counter
        };
    }

    invalidate() {
        this.counter++;
        if (this.controller) {
            this.controller.abort();
            this.controller = null;
        }
    }
}

/**
 * Adresse sur une seule ligne (« 12 rue de la République, 69002 Lyon, France ») à partir d'une adresse structurée ; vide si absente.
 */
export function formatAddressLine(address) {
    if (!address) { return '' }
    return [
        [address.streetNumber, address.street].filter(Boolean).join(' '),
        address.addressLine2,
        [address.postalCode, address.city].filter(Boolean).join(' '),
        address.country
    ].filter(Boolean).join(', ');
}
